/**
 * The two missions flown over your *own* ground: stopping a Zeppelin short of
 * the city it is coming for, and keeping a flight of balloon-busters off your
 * own Drachen.
 *
 * - **Intercept a Zeppelin** is `RBMB.EXE`'s own name for `PS.EXE`'s second,
 *   duplicated HUNT A ZEPPELIN entry, and the only thing that distinguishes it
 *   from *Destroy a Zeppelin* is the defensive framing: the airship is inbound
 *   to a target it has to be stopped short of. That needed the raid model, which
 *   is why the two Zeppelin types were one mission with two names until now.
 * - *"**Balloon Defense** — Protect your observation balloons from the enemy!"*
 *   (docs/game/manual/04-gameplay.md). This one was blocked on nothing but AI
 *   target selection — the roster picked the nearest opposing *aeroplane*, so a
 *   flight sent to burn a gasbag sailed past it looking for a fight.
 *
 * Shared machinery lives in `raid-parts.ts`; the offensive pair is in
 * `generators-raid.ts`.
 */

import { assembleMission } from "./mission-assemble.ts";
import { buildFlight, waypoint } from "./mission-build.ts";
import { missionContext, type MissionOptions } from "./mission-options.ts";
import { scoutsFor } from "./mission-roster.ts";
import type { MissionFlight, MissionSpec, MissionTarget } from "./mission-spec.ts";
import type { MissionObjective } from "./objectives.ts";
import type { RaidPlan } from "./raid.ts";
import {
  FLAK_NOTE,
  GROUND_MODEL_NOTE,
  RAID_PASS_NOTE,
  ZEPPELIN_RAID_ALTITUDE_M,
  playerSection,
  raidBriefingLines,
  type StrikeSite,
} from "./raid-parts.ts";
import { groundTargetPosition } from "../combat/targets.ts";

/** Defended balloons fly lower than the ones a raid goes after: they are over their own lines. */
const DEFENDED_BALLOON_ALTITUDE_M = 1000;

/**
 * The one structure a Zeppelin raid is aimed at. An airship holds a single
 * course all the way in and cannot line itself up on a second aiming point, so
 * giving it a three-building target list would guarantee it "failed" its raid
 * whatever happened — the pass mark is half the targets, and it can only ever
 * reach one of them.
 */
function cityTarget(site: StrikeSite): MissionTarget {
  return {
    id: "target-2",
    kind: "factory",
    side: "friendly",
    position: groundTargetPosition("factory", 0, site.line),
    headingRad: 0,
    label: "Gasworks",
  };
}

const CITY_IDS: readonly string[] = ["target-2"];

const ZEPPELIN_OBJECTIVES: readonly MissionObjective[] = [
  {
    kind: "destroyTargets",
    id: "burn-zeppelin",
    label: "Bring the Zeppelin down short of the city",
    role: "primary",
    count: 1,
    targetIds: ["zeppelin"],
  },
  {
    kind: "protectTargets",
    id: "protect-city",
    label: "Keep the raid off the city",
    role: "survival",
    targetIds: CITY_IDS,
    // No allowance at all: the airship is aimed at one thing, so letting it bomb
    // *anything* is letting the raid succeed.
  },
];

/**
 * **INTERCEPT A ZEPPELIN.** One airship — *"You can have only one zeppelin"* —
 * inbound to a target it must be stopped short of.
 *
 * The airship carries bombs like any other raider and lets them go over the city
 * if it is allowed to get there; a rack does not need a pilot, so it runs
 * through the same release solver a Gotha does. Its own gun positions are still
 * not modelled — the manual has them covering everything except dead astern and
 * abeam, which is also why the classic attack was from directly behind.
 */
export function generateZeppelinIntercept(options: MissionOptions): MissionSpec {
  const context = missionContext(options, "intercept-zeppelin");
  const { draw } = context;
  // A short drift, not a wide one: the airship holds whatever course it came in
  // on, so a wide offset would mean it could never solve for the city at all.
  const drift = draw.between(-90, 90);
  // The city sits close behind the lines and the airship is already near them,
  // because a Zeppelin makes 12 m/s: at the offensive mission's 5 km separation
  // the interception would be seven minutes of straight flying before anything
  // was at stake.
  const site: StrikeSite = { line: 1100 + draw.between(0, 300), spread: draw.between(280, 420) };
  const approachZ = -900;
  const escortAircraft = options.enemyAircraft ?? draw.pick(scoutsFor(context.enemyNation, context.era));
  const escortAltitude = ZEPPELIN_RAID_ALTITUDE_M + 200;

  const targets: readonly MissionTarget[] = [
    cityTarget(site),
    {
      id: "zeppelin",
      kind: "zeppelin",
      side: "hostile",
      position: { x: drift, y: ZEPPELIN_RAID_ALTITUDE_M, z: approachZ },
      // Body forward is −Z (`math3d.ts`), so heading π is what drives a drifting
      // target toward +z — across the lines and on to the city.
      headingRad: Math.PI,
      label: "Zeppelin L 31",
      bombs: { count: 6, targetIds: CITY_IDS },
    },
  ];

  const flights: MissionFlight[] = [
    ...playerSection({
      context,
      position: { x: 0, y: ZEPPELIN_RAID_ALTITUDE_M + 200, z: site.line },
      headingRad: 0,
      wingmen: 1,
      ammunition: options.ammunition ?? "incendiary",
      waypoints: [
        waypoint({ x: drift, y: ZEPPELIN_RAID_ALTITUDE_M + 150, z: approachZ }, "ZEPPELIN"),
        waypoint({ x: 0, y: ZEPPELIN_RAID_ALTITUDE_M, z: site.line }, "CITY"),
      ],
    }),
    buildFlight({
      id: "hostile-1",
      side: "hostile",
      aircraft: escortAircraft,
      callsign: "Escort",
      position: { x: drift - 400, y: escortAltitude, z: approachZ - 400 },
      headingRad: Math.PI,
      skill: "regular",
      waypoints: [waypoint({ x: drift, y: escortAltitude, z: site.line }, "SCREEN")],
    }),
  ];

  return assembleMission(context, {
    catalogId: "intercept-zeppelin",
    type: "interceptZeppelin",
    title: "Intercept a Zeppelin",
    flights,
    targets,
    objectives: ZEPPELIN_OBJECTIVES,
    raid: { role: "intercept", raiderIds: ["zeppelin"], targetIds: CITY_IDS } satisfies RaidPlan,
    briefing: raidBriefingLines({
      context,
      opening:
        "A Zeppelin is coming in over the coast with a scout in company, and the city behind you is what it is coming for.",
      flightSize: 2,
      enemyCount: 1,
      enemyLeader: "the airship's escort",
      enemyAircraft: escortAircraft,
      ownAltitudeM: ZEPPELIN_RAID_ALTITUDE_M + 200,
      enemyAltitudeM: escortAltitude,
    }),
    portNotes: [
      RAID_PASS_NOTE,
      GROUND_MODEL_NOTE,
      FLAK_NOTE,
      "The airship holds one course and one speed all the way in: it has no navigation of its own, so it cannot be driven off, only shot down.",
    ],
  });
}

const BALLOON_IDS: readonly string[] = ["balloon-1", "balloon-2"];

function defendedBalloons(spread: number, line: number): readonly MissionTarget[] {
  return [
    {
      id: "balloon-1",
      kind: "balloon",
      side: "friendly",
      position: { x: -spread, y: DEFENDED_BALLOON_ALTITUDE_M, z: line },
      headingRad: 0,
      label: "Drachen 1",
    },
    {
      id: "balloon-2",
      kind: "balloon",
      side: "friendly",
      position: { x: spread, y: DEFENDED_BALLOON_ALTITUDE_M, z: line - 260 },
      headingRad: 0,
      label: "Drachen 2",
    },
  ];
}

/**
 * **BALLOON DEFENSE.** The mirror of BALLOON BUSTING, and the type that was
 * blocked on nothing but AI target selection. A flight now carries a strike
 * assignment (`strikeTargetIds`) and picks between it and an aeroplane on the
 * route AI's own engage/disengage hysteresis.
 */
export function generateBalloonDefense(options: MissionOptions): MissionSpec {
  const context = missionContext(options, "balloon-defense");
  const { draw } = context;
  const spread = draw.between(400, 700);
  const line = 1200 + draw.between(0, 400);
  const raiderCount = draw.int(2, 3);
  const raiderAircraft = options.enemyAircraft ?? draw.pick(scoutsFor(context.enemyNation, context.era));
  const raiderAltitude = DEFENDED_BALLOON_ALTITUDE_M + 500;

  const flights: MissionFlight[] = [
    ...playerSection({
      context,
      position: { x: 0, y: raiderAltitude, z: line - 900 },
      headingRad: 0,
      wingmen: 2,
      ammunition: options.ammunition,
      waypoints: [
        waypoint({ x: -spread, y: raiderAltitude, z: line }, "DRACHEN 1"),
        waypoint({ x: spread, y: raiderAltitude, z: line - 260 }, "DRACHEN 2"),
      ],
    }),
    ...Array.from({ length: raiderCount }, (_unused, index) =>
      buildFlight({
        id: `hostile-${index + 1}`,
        side: "hostile",
        aircraft: raiderAircraft,
        callsign: `Raider ${index + 1}`,
        position: { x: spread - index * 340, y: raiderAltitude + index * 70, z: line - 2400 - index * 200 },
        headingRad: 0,
        skill: index === 0 ? "veteran" : "regular",
        // Incendiary, because that is what a balloon-burner loads and what the
        // Mission Builder manual tells its own author to give them.
        ammunition: "incendiary",
        // Split the pair, so both gasbags are actually threatened.
        strikeTargetIds: index % 2 === 0 ? BALLOON_IDS : [...BALLOON_IDS].reverse(),
        waypoints: [
          waypoint({ x: index % 2 === 0 ? -spread : spread, y: DEFENDED_BALLOON_ALTITUDE_M + 200, z: line }, "ATTACK"),
        ],
      }),
    ),
  ];

  return assembleMission(context, {
    catalogId: "balloon-defense",
    type: "balloonDefense",
    title: "Balloon defense",
    flights,
    targets: defendedBalloons(spread, line),
    objectives: [
      {
        kind: "destroyAircraft",
        id: "drive-off",
        label: "Drive off the balloon-strafers",
        role: "primary",
        // Breaking the flight up is enough; the survivor of a mauled Kette does
        // not press on to burn a Drachen, and requiring every last one turns a
        // defence into an extermination.
        count: Math.max(1, raiderCount - 1),
      },
      {
        kind: "protectTargets",
        id: "protect-balloons",
        label: "Both Drachen must survive",
        role: "survival",
        targetIds: BALLOON_IDS,
      },
    ],
    briefing: raidBriefingLines({
      context,
      opening: "Two of our Drachen are up over the sector and a flight of scouts is coming for them with incendiary.",
      flightSize: 3,
      enemyCount: raiderCount,
      enemyLeader: "their balloon-busters",
      enemyAircraft: raiderAircraft,
      ownAltitudeM: raiderAltitude,
      enemyAltitudeM: raiderAltitude,
    }),
    portNotes: [
      FLAK_NOTE,
      "The balloon's winch is not modelled: a real crew hauled the envelope down the moment a scout appeared, and these two wait where they are.",
      "The attacking flight goes for whichever of the two it was assigned and breaks off to fight anything that gets inside the AI's engage range — but beyond skipping a gasbag another raider has already set alight, it does not re-plan.",
    ],
  });
}
